import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { EFFECT_DEFAULTS } from "src/components/media/AudioEffectsWindowContent"

const AudioPlaybackContext = createContext(null)

export const AudioPlaybackProvider = ({ children }) => {
  const audioRef = useRef(null)
  const audioGraphRef = useRef(null)
  const activePlaylistRef = useRef({ id: null, tracks: [], index: 0 })
  const [activePlaylistId, setActivePlaylistId] = useState(null)
  const [tracks, setTracks] = useState([])
  const [trackIndex, setTrackIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(NaN)
  const [effects, setEffects] = useState(EFFECT_DEFAULTS)

  const ensureAudioGraph = useCallback(async () => {
    if (audioGraphRef.current) {
      await audioGraphRef.current.context.resume()
      return
    }
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass || !audioRef.current) return

    const context = new AudioContextClass()
    const source = context.createMediaElementSource(audioRef.current)
    const volumeGain = context.createGain()
    const warbleGain = context.createGain()
    const warbleOscillator = context.createOscillator()
    const warbleDepth = context.createGain()

    volumeGain.gain.value = effects.volume / 100
    warbleGain.gain.value = 1
    warbleOscillator.type = "sine"
    warbleOscillator.frequency.value = 0.4 + (effects.warble / 100) * 7.6
    warbleDepth.gain.value = (effects.warble / 100) * 0.45
    source.connect(volumeGain)
    volumeGain.connect(warbleGain)
    warbleGain.connect(context.destination)
    warbleOscillator.connect(warbleDepth)
    warbleDepth.connect(warbleGain.gain)
    warbleOscillator.start()

    audioRef.current.playbackRate = 2 ** (effects.pitch / 12)
    audioGraphRef.current = { context, volumeGain, warbleGain, warbleOscillator, warbleDepth }
    await context.resume()
  }, [effects.pitch, effects.volume, effects.warble])

  const selectTrack = useCallback((playlistId, playlistTracks, index) => {
    if (!playlistTracks.length || !audioRef.current) return null
    const normalizedIndex = (index + playlistTracks.length) % playlistTracks.length
    const nextTrack = playlistTracks[normalizedIndex]
    const isSameTrack = activePlaylistRef.current.id === playlistId
      && activePlaylistRef.current.index === normalizedIndex

    activePlaylistRef.current = { id: playlistId, tracks: playlistTracks, index: normalizedIndex }
    setActivePlaylistId(playlistId)
    setTracks(playlistTracks)
    setTrackIndex(normalizedIndex)
    if (!isSameTrack) {
      audioRef.current.src = nextTrack.src
      setCurrentTime(0)
      setDuration(NaN)
    }
    return nextTrack
  }, [])

  const play = useCallback(async (playlistId, playlistTracks, index) => {
    const track = selectTrack(playlistId, playlistTracks, index)
    if (!track || !audioRef.current) return
    const graphReady = ensureAudioGraph()
    const playbackStarted = audioRef.current.play()
    await Promise.all([graphReady, playbackStarted])
  }, [ensureAudioGraph, selectTrack])

  const changeTrack = useCallback(async (playlistId, playlistTracks, index, autoplay = false) => {
    const track = selectTrack(playlistId, playlistTracks, index)
    if (!track || !autoplay || !audioRef.current) return
    const graphReady = ensureAudioGraph()
    const playbackStarted = audioRef.current.play()
    await Promise.all([graphReady, playbackStarted])
  }, [ensureAudioGraph, selectTrack])

  const pause = useCallback(() => audioRef.current?.pause(), [])
  const stop = useCallback(() => {
    if (!audioRef.current) return
    audioRef.current.pause()
    audioRef.current.currentTime = 0
    setCurrentTime(0)
  }, [])

  const setEffect = useCallback((name, value) => {
    setEffects((current) => ({ ...current, [name]: value }))
    const graph = audioGraphRef.current
    if (name === "volume" && graph) {
      graph.volumeGain.gain.setTargetAtTime(value / 100, graph.context.currentTime, 0.02)
    }
    if (name === "pitch" && audioRef.current) {
      audioRef.current.playbackRate = 2 ** (value / 12)
    }
    if (name === "warble" && graph) {
      graph.warbleOscillator.frequency.setTargetAtTime(0.4 + (value / 100) * 7.6, graph.context.currentTime, 0.02)
      graph.warbleDepth.gain.setTargetAtTime((value / 100) * 0.45, graph.context.currentTime, 0.02)
    }
  }, [])

  const handleEnded = useCallback(() => {
    const active = activePlaylistRef.current
    if (!active.tracks.length) return
    const nextIndex = (active.index + 1) % active.tracks.length
    changeTrack(active.id, active.tracks, nextIndex, true)
  }, [changeTrack])

  useEffect(() => () => {
    try {
      audioGraphRef.current?.warbleOscillator.stop()
    } catch {}
    audioGraphRef.current?.context.close()
  }, [])

  const value = useMemo(() => ({
    activePlaylistId,
    tracks,
    trackIndex,
    currentTrack: tracks[trackIndex] || null,
    isPlaying,
    currentTime,
    duration,
    effects,
    play,
    changeTrack,
    pause,
    stop,
    setEffect,
  }), [activePlaylistId, changeTrack, currentTime, duration, effects, isPlaying, pause, play, setEffect, stop, trackIndex, tracks])

  return (
    <AudioPlaybackContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        preload="metadata"
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        onTimeUpdate={(event) => setCurrentTime(Math.floor(event.currentTarget.currentTime))}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={handleEnded}
      />
    </AudioPlaybackContext.Provider>
  )
}

export const useAudioPlayback = () => {
  const context = useContext(AudioPlaybackContext)
  if (!context) throw new Error("useAudioPlayback must be used within AudioPlaybackProvider")
  return context
}
